import { Box, Container, Text, Heading, VStack } from "@chakra-ui/react";
import { Helmet } from 'react-helmet-async';
import ServiceDetailsHeader from "../../components/Common/ImageHeader";
import ServicesGrid from "../../components/Common/ServicesGrid";
import CaseStudySection from "../../components/Common/CaseStudySection";
import ExploreSection from "../../components/ProductDetails/ExploreSection";

const ManagedServicesPage = () => {
  // Dynamic services data
  const managedServices = [
    {
      id: 1,
      icon: "/svg/24_7.svg",
      title: "Always-On Availability:",
      description: " 24/7 system uptime, monitoring, and proactive resolution",
      isTopRow: true,
      isRightmost: false,
    },
    {
      id: 2,
      icon: "/svg/24_7.svg",
      title: "Multi-Tier Support:",
      description:
        "Helpdesk, incident tracking, on-site technical assistance, and SLA-based service delivery",
      isTopRow: true,
      isRightmost: false,
    },
    {
      id: 3,
      icon: "/svg/engagement.svg",
      title: "Continuous User Engagement:",
      description:
        "Real-time issue resolution, training, and citizen/user helpdesk management",
      isTopRow: true,
      isRightmost: true,
    },
    {
      id: 4,
      icon: "/svg/end_to_end.svg",
      title: "End-to-End System Administration:",
      description:
        "Infrastructure hosting, application maintenance, and security governance",
      isTopRow: false,
      isRightmost: false,
    },
    {
      id: 5,
      icon: "/svg/analytics.svg",
      title: "Reporting & Analytics:",
      description:
        "Real-time dashboards and performance indicators for decision-making and audits",
      isTopRow: false,
      isRightmost: false,
    },
    {
      id: 6,
      icon: "/svg/management.svg",
      title: "Change Management & Scaling:",
      description:
        "Adapting the system with evolving user needs and regulatory changes",
      isTopRow: false,
      isRightmost: true,
    },
  ];

  // Hajj Management System features
  const hajjFeatures = [
    {
      id: 1,
      icon: "/svg/24_7_2.svg",
      title: "24/7 support during the Hajj season",
    },
    {
      id: 2,
      icon: "/svg/RealTime_Processing.svg",
      title: "Real-time monitoring of enrollment and processing queues",
    },
    {
      id: 3,
      icon: "/svg/multilingual.svg",
      title: "Multilingual citizen helpdesk with live support",
    },
    {
      id: 4,
      icon: "/svg/integration.svg",
      title: "Integration with immigration, airline, and medical systems",
    },
  ];

  // BIDA One Stop Service features
  const bidaFeatures = [
    {
      id: 1,
      icon: "/svg/24_7_2.svg",
      title: "SLA-based service delivery across agencies",
    },
    {
      id: 2,
      icon: "/svg/RealTime_Processing.svg",
      title: "Technical operation of payment gateways, portals, and dashboards",
    },
    {
      id: 3,
      icon: "/svg/multilingual.svg",
      title: "Multilingual investor support & training",
    },
    {
      id: 4,
      icon: "/svg/integration.svg",
      title: "Regular platform updates and integration rollouts",
    },
  ];

  return (
    <>
      <Helmet>
        {/* Basic Meta Tags */}
        <title>Managed Services | Trusted IT Operations Partner for Government & OSS Platforms in Bangladesh</title>
        <meta 
          name="description" 
          content="Business Automation offers trusted Managed Services for Bangladesh's national platforms like Hajj and BIDA OSS—ensuring 24/7 support, secure operations, and citizen-first digital service delivery." 
        />
        <meta 
          name="keywords" 
          content="Managed services Bangladesh, eGovernment platform operation, OSS platform Bangladesh, Queue Pro management system, Citizen services outsourcing, digital service delivery Bangladesh, SLA-based IT operations, BIDA OSS managed partner, Hajj system digital solution, 24/7 IT support government, donor-funded digital project management" 
        />
        <link rel="canonical" href="https://www.ba-systems.com/services/managed-services" />
        <meta name="robots" content="index, follow" />

        {/* Open Graph / Facebook */}
        <meta property="og:url" content="https://ba-systems.com/services/managed-services" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Managed Services | Trusted IT Operations Partner for Government & OSS Platforms in Bangladesh" />
        <meta property="og:description" content="Business Automation offers trusted Managed Services for Bangladesh's national platforms like Hajj and BIDA OSS—ensuring 24/7 support, secure operations, and citizen-first digital service delivery." />
        <meta property="og:image" content="https://opengraph.b-cdn.net/production/images/e1930f8a-3aa2-4fe1-b6ea-0ad5030ebc97.png?token=vTkCXbq7qBnee_uxOFTj3-OKo7vP2KVzX66L6BYKzho&height=596&width=1200&expires=33297038875" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="twitter:domain" content="ba-systems.com" />
        <meta property="twitter:url" content="https://ba-systems.com/services/managed-services" />
        <meta name="twitter:title" content="Managed Services | Trusted IT Operations Partner for Government & OSS Platforms in Bangladesh" />
        <meta name="twitter:description" content="Business Automation offers trusted Managed Services for Bangladesh's national platforms like Hajj and BIDA OSS—ensuring 24/7 support, secure operations, and citizen-first digital service delivery." />
        <meta name="twitter:image" content="https://opengraph.b-cdn.net/production/images/e1930f8a-3aa2-4fe1-b6ea-0ad5030ebc97.png?token=vTkCXbq7qBnee_uxOFTj3-OKo7vP2KVzX66L6BYKzho&height=596&width=1200&expires=33297038875" />
      </Helmet>
      
      <ServiceDetailsHeader
        title="Managed Services"
        subtitle=" Delivering Seamless Digital Operations with Trust and Accountability"
        backgroundImage="/png/bg_manage.png"
        breadcrumbItems={[
          { label: "Home", href: "/" },
          { label: "Services", href: "/our-services" },
          { label: "Managed Services", href: "#", isCurrentPage: true },
        ]}
      />

      <Box
        backgroundImage={"/png/background.png"}
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundRepeat="no-repeat"
        minH="100vh"
        w="full"
      >
        <Box py={{ base: "20px", md: "40px" }}>
          <Container maxW="7xl" px={{ base: 4, md: 6, lg: 8 }}>
            <VStack spacing={{ base: 6, md: 8 }} align="start">
              <VStack spacing={{ base: 3, md: 4 }} align="start" w="full">
                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color="gray.600"
                  lineHeight="1.6"
                  textAlign="justify"
                  px={{ base: 2, md: 0 }}
                >
                  At Business Automation Limited, we offer Managed Services that go
                  beyond traditional IT outsourcing. With over 25 years of
                  experience and successful partnerships across government and
                  enterprise, we manage mission-critical systems
                  end-to-end—ensuring continuous performance, operational
                  efficiency, and measurable results.
                </Text>
                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color="gray.600"
                  lineHeight="1.6"
                  textAlign="justify"
                  px={{ base: 2, md: 0 }}
                >
                  Whether you're running a national service portal or a
                  large-scale citizen-facing system, our Managed Services model
                  ensures you stay focused on your core mission—while we take
                  care of the technology, support, and user experience.
                </Text>
              </VStack>
            </VStack>
            {/* What We Offer Under Managed Services Section */}
            <VStack spacing={{ base: 3, md: 4 }} align="center" mt={{ base: 12, md: 16 }} px={{ base: 2, md: 0 }}>
              <Heading
                fontSize={{ base: "xl", md: "2xl", lg: "3xl" }}
                fontWeight="bold"
                color="gray.800"
                textAlign="center"
                px={{ base: 2, md: 0 }}
              >
                {"What We Offer Under"}{" "}
                <Text as="span" color="#000B8C">
                  {"Managed Services"}
                </Text>
              </Heading>
              <Text
                fontSize={{ base: "sm", md: "md", lg: "lg" }}
                color="gray.600"
                maxW="4xl"
                textAlign="center"
                px={{ base: 2, md: 0 }}
              >
                {
                  "Our managed services framework is purpose-built for public and enterprise systems that require:"
                }
              </Text>
            </VStack>
            <ServicesGrid
              // title="What We Offer Under"
              // highlightText="Managed Services"
              // description="Our managed services framework is purpose-built for public and enterprise systems that require:"
              services={managedServices}
            />
            <Heading
              fontSize={{ base: "xl", md: "2xl", lg: "3xl" }}
              fontWeight="bold"
              color="#000B8C"
              mt={{ base: 12, md: 16 }}
              py={{ base: 6, md: 8 }}
              textAlign="center"
              px={{ base: 2, md: 0 }}
            >
              <Text as="span" color="gray.800">
                Flagship {""}
              </Text>
              Success Stories
            </Heading>
            <VStack spacing={{ base: 3, md: 4 }} align="center" px={{ base: 2, md: 0 }}>
              <Heading
                fontSize={{ base: "lg", md: "xl", lg: "2xl" }}
                fontWeight="bold"
                color="gray.800"
                textAlign="center"
              >
                {"Hajj Management System"}{" "}
                <Text as="span" color="#000B8C">
                  {"Serving 100,000+ Pilgrims Annually"}
                </Text>
              </Heading>
              <Text
                fontSize={{ base: "sm", md: "md", lg: "lg" }}
                color="gray.600"
                maxW="7xl"
                textAlign="justify"
                px={{ base: 2, md: 0 }}
              >
                {
                  "Business Automation's fully managed Queue-Pro platform powers the Digital Hajj Registration and Pilgrim Services in Bangladesh. From biometrics, scheduling, and document processing to queue management and reporting, we handle it all—ensuring peace of mind for both administrators and pilgrims during the most critical season."
                }
              </Text>
            </VStack>

            {/* Hajj Management System Case Study */}
            <CaseStudySection
              // title="Hajj Management System"
              // subtitle="Serving 100,000+ Pilgrims Annually"
              // description="Business Automation's fully managed Queue-Pro platform powers the Digital Hajj Registration and Pilgrim Services in Bangladesh. From biometrics, scheduling, and document processing to queue management and reporting, we handle it all—ensuring peace of mind for both administrators and pilgrims during the most critical season."
              features={hajjFeatures}
            />
            <Box h={{ base: 8, md: 16 }} />
            <VStack spacing={{ base: 3, md: 4 }} align="center" px={{ base: 2, md: 0 }}>
              <Heading
                fontSize={{ base: "lg", md: "xl", lg: "2xl" }}
                fontWeight="bold"
                color="gray.800"
                textAlign="center"
              >
                {"BIDA One Stop Service (OSS)"}{" "}
                <Text as="span" color="#000B8C">
                  {"National Investment Facilitation Hub"}
                </Text>
              </Heading>
              <Text
                fontSize={{ base: "sm", md: "md", lg: "lg" }}
                color="gray.600"
                maxW="7xl"
                textAlign="justify"
                px={{ base: 2, md: 0 }}
              >
                {
                  "We are the official Managed Services partner of the Bangladesh Investment Development Authority (BIDA) OSS platform, supporting 50+ agencies with real-time investor services and API-driven automation. From onboarding to aftercare, we ensure smooth investor journeys and continuous compliance."
                }
              </Text>
            </VStack>

            {/* BIDA One Stop Service Case Study */}
            <CaseStudySection
              title=""
              subtitle=""
              features={bidaFeatures}
              bgColor="white"
            />
            <Heading
              pt={{ base: 6, md: 8 }}
              px={{ base: 2, md: 0 }}
              maxW="7xl"
              fontSize={{ base: "lg", md: "xl", lg: "2xl" }}
              fontWeight="bold"
              color="gray.800"
              textAlign="center"
            >
              Why Choose{" "}
              <Text as="span" color="#000B8C">
                Business Automation’s Managed Services
              </Text>
            </Heading>
            <Box w="full" display="flex" justifyContent="center" py={{ base: 6, md: 8 }} px={{ base: 2, md: 0 }}>
              <Box
                as="img"
                src="/png/manage.png"
                alt="AI-driven cybersecurity capabilities"
                maxW="100%"
                height="auto"
                borderRadius="lg"
                loading="lazy"
                draggable="false"
              />
            </Box>
            <Text
              fontSize={{ base: "sm", md: "md", lg: "lg" }}
              color="gray.600"
              lineHeight="1.6"
              fontWeight={"bold"}
              fontStyle={"italic"}
              textAlign="center"
              px={{ base: 4, md: 0 }}
            >
              We are not just your technology provider—we are your strategic
              partner in delivering inclusive, secure, and responsive services
              to citizens and businesses.
            </Text>
          </Container>
        </Box>

        <ExploreSection />
      </Box>
    </>
  );
};

export default ManagedServicesPage;
